// ===========================================================================
// Market Access – Moonlake: one dataset per page
//
// Page 1 (Coverage) reads src/assets/json/market-access-moonlake-data.json: a
// single, flat array of objects — one record per (indication, snapshot, MMIT
// formulary, territory, drug, Biologics Count bucket) with the covered lives in it:
//
//   { "therapeutic_class": "Immunology", "indication": "HS",
//     "snapshot": "Jan 2026", "snapshot_dt": "2026-01-01",
//     "channel": "Commercial", "pbm": "OptumRx", "controller": "UnitedHealthcare",
//     "plan": "Plan 1", "formulary": "Formulary 2", "territory": "Northeast",
//     "drug": "Best Adalimumab", "drug_class": "TNFs",
//     "bucket": "1L", "bucket_type": "Line", "lives": 48030 }
//
// pbm → controller → plan → formulary is MMIT's payer hierarchy, which the Payer
// filter shows as a tree. Cigna is a controller under Express Scripts, not a PBM.
//
// Nothing is pre-aggregated. Every filter and widget derives what it shows from
// this array through configuration (market-access-moonlake.config.ts) and the
// generic engine in moonlake-aggregate.ts — so a new widget, or a new page, is a
// new config entry rather than a new data shape. When the API is ready it only
// has to return the same array.
// ===========================================================================

export interface MoonlakeRecord {
  /** The class in scope — a display chip; fixed for the release. */
  therapeutic_class: string;
  indication: string;
  /** Display label of the snapshot, e.g. "Jan 2026". */
  snapshot: string;
  /** Sortable date of the snapshot (ISO), which orders snapshots chronologically. */
  snapshot_dt: string;
  channel: string;
  /** MMIT payer hierarchy, top to bottom. */
  pbm: string;
  controller: string;
  plan: string;
  formulary: string;
  /** Zip2Terr territory. */
  territory: string;
  /** DrugClean. */
  drug: string;
  /** Class of Drug, from MoonLake's All Drug Classes.csv (TNFs, IL17, IL23, JAKs, Other). */
  drug_class: string;
  /**
   * Biologics Count bucket. A "Line" bucket is "<n>L" for any n ≥ 1 (1L, 2L,
   * 3L, 4L, …); a "Status" bucket is one of STATUS_BUCKETS in the config.
   */
  bucket: string;
  /** "Line" when `bucket` is a line of therapy (<n>L), "Status" otherwise. */
  bucket_type: string;
  lives: number;
}

export const MOONLAKE_DATA_URL = "assets/json/market-access-moonlake-data.json";

/** Guard the payload: an array of records, with `lives` coerced to a number. */
export function normalizeMoonlakeData(payload: any): MoonlakeRecord[] {
  return normalizeRecords<MoonlakeRecord>(payload, ["lives"]);
}

// ===========================================================================
// Pages 2 to 4 read one flat array each, the way page 1 does — each page's
// file carries the MMIT records (the filters and the Date range work over them)
// plus whatever else that page shows, every record tagged with its `source`.
// ===========================================================================

/**
 * What kind of record a row is — also a widget's `source` in the config:
 *
 *   "mmit"        — a MoonlakeRecord (pages 3 and 4: a CompetitiveRecord);
 *   "analog"      — an AnalogLaunchRecord (page 2);
 *   "addressable" — an AddressableRecord (page 2);
 *   "rx"          — an RxRecord (page 4);
 *   "switch"      — a SwitchRecord (page 4);
 *   "reject"      — a RejectRecord (page 5).
 */
export type RecordSource = "mmit" | "analog" | "addressable" | "rx" | "switch" | "reject";

/** A page's array, split by `source`: the MMIT records, and every other kind by name. */
export interface PageData {
  mmit: MoonlakeRecord[];
  sources: Partial<Record<RecordSource, any[]>>;
}

const bySource = (payload: any) => {
  const rows: any[] = Array.isArray(payload) ? payload : [];
  return (source: RecordSource) => rows.filter((r) => r && r.source === source);
};

/**
 * Page 2 (Access & Coverage): src/assets/json/market-access-moonlake-access-coverage.json.
 * "mmit", "analog" and "addressable" records. The last two — a launch history
 * and a claims count — cannot be derived from the MMIT records, so they travel
 * beside them.
 */

export const ACCESS_DATA_URL = "assets/json/market-access-moonlake-access-coverage.json";

/**
 * Analog launch coverage: an analog brand's lives-weighted coverage at each
 * month since its launch, per indication it launched into.
 *
 *   { "source": "analog", "brand": "Cosentyx", "drug_class": "IL17",
 *     "indication": "PsO", "month": 6, "month_label": "M6", "status": "Actual",
 *     "covered_lives": 104000000, "lives": 226000000 }
 *
 * "[Brand X]" is the client's own product — its curve is a forecast.
 */
export interface AnalogLaunchRecord {
  brand: string;
  drug_class: string;
  indication: string;
  month: number;
  month_label: string;
  /** "Actual" or "Forecast". */
  status: string;
  covered_lives: number;
  lives: number;
}

/**
 * Addressable market (claims): patients by quarter, channel, territory and
 * line-of-therapy position, split by whether their plan gives the focus brand
 * unrestricted access.
 *
 *   { "source": "addressable", "indication": "HS", "period": "Q2 2026",
 *     "period_dt": "2026-04-01", "channel": "Commercial", "territory": "Northeast",
 *     "position": "1L", "access": "Access-Adjusted Addressable", "patients": 812 }
 *
 * Claims carry the channel but no MMIT plan id, so they honour the Payer filter
 * at channel level only (until the MMIT-to-claims plan bridge exists).
 */
export interface AddressableRecord {
  indication: string;
  period: string;
  /** Quarter start (ISO). */
  period_dt: string;
  channel: string;
  territory: string;
  position: string;
  access: string;
  patients: number;
}

export function normalizeAccessData(payload: any): PageData {
  const of = bySource(payload);
  return {
    mmit: normalizeMoonlakeData(of("mmit")),
    sources: {
      analog: normalizeRecords<AnalogLaunchRecord>(of("analog"), ["month", "covered_lives", "lives"]),
      addressable: normalizeRecords<AddressableRecord>(of("addressable"), ["patients"]),
    },
  };
}

/**
 * Page 3 (Competitive Position): src/assets/json/market-access-moonlake-competitive-position.json.
 * The MMIT records, each carrying its formulary's coverage of the drug, the
 * access position and the step-through requirement as MMIT words it — so the
 * step table's lives answer every filter (Territory included).
 *
 *   { ...a MoonlakeRecord,
 *     "coverage": "Covered, PA required", "access_position": "2L",
 *     "step_text": "Trial of 1 TNF inhibitor" }
 */
export interface CompetitiveRecord extends MoonlakeRecord {
  coverage: string;
  access_position: string;
  step_text: string;
}

export const COMPETITIVE_DATA_URL = "assets/json/market-access-moonlake-competitive-position.json";

export function normalizeCompetitiveData(payload: any): PageData {
  return { mmit: normalizeRecords<CompetitiveRecord>(payload, ["lives"]), sources: {} };
}

/**
 * Page 4 (Real-World Evidence): src/assets/json/market-access-moonlake-real-world-evidence.json.
 * "mmit" records (as page 3's, for the plans' MMIT status), "rx" and "switch".
 */
export const RWE_DATA_URL = "assets/json/market-access-moonlake-real-world-evidence.json";

/**
 * Claims: per quarter, indication, MMIT formulary, territory and drug, the Rx
 * requests, how many were approved, how many hit a restriction (rejected, PA or
 * step edit) and the new-to-brand / total prescriptions. Carries the full payer
 * path, so it honours every filter.
 *
 *   { "source": "rx", "therapeutic_class": "Immunology", "indication": "HS",
 *     "period": "Q2 2026", "period_dt": "2026-04-01", "channel": "Commercial",
 *     "pbm": "OptumRx", "controller": "UnitedHealthcare", "plan": "Plan 1",
 *     "formulary": "Formulary 2", "territory": "Northeast", "drug": "Bimzelx",
 *     "drug_class": "IL17", "requests": 22, "approved": 19, "restricted": 6,
 *     "nbrx": 6, "trx": 44 }
 */
export interface RxRecord {
  therapeutic_class: string;
  indication: string;
  period: string;
  /** Quarter start (ISO). */
  period_dt: string;
  channel: string;
  pbm: string;
  controller: string;
  plan: string;
  formulary: string;
  territory: string;
  drug: string;
  drug_class: string;
  requests: number;
  approved: number;
  restricted: number;
  nbrx: number;
  trx: number;
  /** Derived on load: "<controller> / <plan>", one plan's label. */
  payer_plan: string;
}

/**
 * Switching: per quarter, indication, channel and territory, the patients who
 * moved from one drug to another and their months on the first before moving
 * (summed, so an average is months_on_therapy ÷ switched).
 *
 *   { "source": "switch", "indication": "HS", "period": "Q2 2026",
 *     "period_dt": "2026-04-01", "channel": "Commercial", "territory": "Northeast",
 *     "from_drug": "Skyrizi", "to_drug": "Cimzia", "switched": 9,
 *     "months_on_therapy": 30.4 }
 */
export interface SwitchRecord {
  indication: string;
  period: string;
  period_dt: string;
  channel: string;
  territory: string;
  from_drug: string;
  to_drug: string;
  switched: number;
  months_on_therapy: number;
  /** Derived on load: "<from_drug> to <to_drug>". */
  pair: string;
}

/** One plan's label: plan names repeat across controllers, so the controller leads. */
export function payerPlanLabel(r: { controller: string; plan: string }): string {
  return `${r.controller} / ${r.plan}`;
}

export function normalizeRweData(payload: any): PageData {
  const of = bySource(payload);
  const rx = normalizeRecords<RxRecord>(of("rx"), ["requests", "approved", "restricted", "nbrx", "trx"]);
  rx.forEach((r) => (r.payer_plan = payerPlanLabel(r)));
  const sw = normalizeRecords<SwitchRecord>(of("switch"), ["switched", "months_on_therapy"]);
  sw.forEach((r) => (r.pair = `${r.from_drug} to ${r.to_drug}`));
  return {
    mmit: normalizeRecords<CompetitiveRecord>(of("mmit"), ["lives"]),
    sources: { rx, switch: sw },
  };
}

/**
 * Page 5 (Utilization Management): src/assets/json/market-access-moonlake-utilization-management.json.
 * "mmit" records and "reject" records.
 */
export const UM_DATA_URL = "assets/json/market-access-moonlake-utilization-management.json";

/**
 * Page 5's MMIT records: page 3's, plus the formulary's quantity limit on the
 * drug and the same three facts on its parent PBM's national formulary (same
 * snapshot, indication and drug) — what a custom / downstream formulary is
 * compared with.
 *
 *   { ...a CompetitiveRecord,
 *     "quantity_limit": "30-day supply cap",
 *     "national_coverage": "Covered", "national_position": "1L",
 *     "national_quantity_limit": "None" }
 */
export interface UmRecord extends CompetitiveRecord {
  quantity_limit: string;
  national_coverage: string;
  national_position: string;
  national_quantity_limit: string;
}

/**
 * Claims rejected (and of those, abandoned) per quarter, indication, channel,
 * territory and rejection reason, market-wide (every drug in the class).
 *
 *   { "source": "reject", "indication": "HS", "period": "Q2 2026",
 *     "period_dt": "2026-04-01", "channel": "Commercial", "territory": "Northeast",
 *     "reason": "PA Required", "rejections": 58, "abandonments": 19 }
 */
export interface RejectRecord {
  indication: string;
  period: string;
  period_dt: string;
  channel: string;
  territory: string;
  reason: string;
  rejections: number;
  abandonments: number;
}

export function normalizeUmData(payload: any): PageData {
  const of = bySource(payload);
  return {
    mmit: normalizeRecords<UmRecord>(of("mmit"), ["lives"]),
    sources: { reject: normalizeRecords<RejectRecord>(of("reject"), ["rejections", "abandonments"]) },
  };
}

/** An array of objects, with the listed fields coerced to numbers. */
function normalizeRecords<T>(payload: any, numeric: string[]): T[] {
  if (!Array.isArray(payload)) return [];
  return payload
    .filter((r) => r && typeof r === "object")
    .map((r) => {
      const out = { ...r };
      numeric.forEach((k) => (out[k] = Number(r[k]) || 0));
      return out as T;
    });
}
